import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowAltCircleRight, FaChevronRight } from 'react-icons/fa';
import { AiFillClockCircle } from 'react-icons/ai';
import dayjs from 'dayjs';

import { Button } from 'playup-web-core';

import UserPic from './UserPic';
import RewardSection from './RewardSection';
import formatCurrency from '../../../utils/formatCurrency';
import { dispatchPushUrl } from '../../../utils/eventDispatchers';
import { ClientAppConfig } from '../../../appsConfig';
import useGetLimit from '../../../Account/components/Deposit/useGetLimit';

interface Props {
	activeApp: ClientAppConfig;
	bonuses: Array<{
		amount: number;
		name: string;
		icon: string;
		type: string;
	}>;
	linkCounters: Array<{
		key: string;
		count: number;
	}>;
	hideBalance: boolean;
	isSmallScreen: boolean;
	toggleHideBalance: () => void;
	toggleMenu: () => void;
	user: PortalUser;
	logout: () => void;
	sessionStartTime?: string;
}

const MenuList = ({
	activeApp,
	hideBalance,
	isSmallScreen,
	bonuses,
	linkCounters,
	toggleHideBalance,
	toggleMenu,
	user,
	logout,
	sessionStartTime,
}: Props) => {
	const history = useHistory();
	const [sessionTimeText, setSessionTimeText] = React.useState<string | null>(null);

	const onDeposit = React.useCallback(() => {
		history.replace('?modal=deposit');
		toggleMenu();
	}, [history, toggleMenu]);

	const onWithdraw = React.useCallback(() => {
		history.push('/account/withdraw');
		toggleMenu();
	}, [history, toggleMenu]);

	const handleLinkClick = (url) => () => {
		toggleMenu();
		const baseurl = url.split('/')[1];
		history.push(url);

		// dispatching route to client apps
		if (['betting', 'fantasy'].includes(baseurl)) {
			dispatchPushUrl(url);
		}
	};

	React.useEffect(() => {
		if (!sessionStartTime) {
			setSessionTimeText(null);
			return;
		}

		const updateTime = () => {
			const now = dayjs();
			const seconds = now.diff(dayjs(sessionStartTime), 'seconds') % 60;
			const mins = now.diff(dayjs(sessionStartTime), 'minutes') % 60;
			const hours = now.diff(dayjs(sessionStartTime), 'hour') % 24;
			const days = now.diff(dayjs(sessionStartTime), 'day');

			setSessionTimeText(
				`Current Session Time: ${days > 0 ? days + 'd ' : ''}${hours > 0 ? hours + 'h' : '0h'} ${
					mins > 0 ? mins + 'min' : '0min'
				} ${seconds}s`
			);
		};

		updateTime();

		const intervalRef = setInterval(updateTime, 1000);

		return () => clearInterval(intervalRef);
	}, [sessionStartTime]);

	const handleUpdateAvatar = () => {
		history.push('?modal=avatar-upload');
		toggleMenu();
	};
	const { limitItem } = useGetLimit();
	return (
		<MenuWrapper $fullScreen={isSmallScreen}>
			<MenuRow>
				{user.avatar?.small && <UserPic src={user.avatar.small} onClick={handleUpdateAvatar} />}

				<NameWrapper>
					<strong>
						{user.first_name} {user.last_name}
					</strong>
					<Pill $color={user.id_verified ? 'green' : 'red'}>{user.id_verified ? 'Verified' : 'Unverified'}</Pill>
					<br />
					<i>@{user.name}</i>
				</NameWrapper>
				{!user.id_verified && (
					<LinkWrapper>
						<UnverifiedLink to="?modal=verify">Verify Now</UnverifiedLink>
					</LinkWrapper>
				)}
			</MenuRow>
			<MenuRow style={{ padding: '5px 16px' }}>
				<RewardSection
					displayBalance={formatCurrency(user.balance)}
					bonuses={bonuses}
					hideBalance={hideBalance}
					toggleHideBalance={toggleHideBalance}
				/>
			</MenuRow>
			<MenuRow style={{ justifyContent: 'space-between' }}>
				<Button kind="white" height={50} style={{ flexBasis: '49%' }} onClick={onDeposit}>
					Deposit
				</Button>
				<Button kind="hollow-black" height={50} style={{ flexBasis: '49%' }} onClick={onWithdraw}>
					Withdraw
				</Button>
			</MenuRow>
			{activeApp.balanceLinks.map(({ icon, name, url, counterKey }) => {
				const counter = linkCounters.find((counter) => counter.key === counterKey)?.count ?? null;
				return (
					<MenuRow key={name}>
						<StyledLink onClick={handleLinkClick(url)}>
							<div>
								{icon && <img src={icon} alt={name} />}
								{name}
								{counter !== null ? ` (${counter})` : ''}
								{name === 'Deposit Limit' && !limitItem && <Pill $color="orange">Set Limit</Pill>}
							</div>
							<FaChevronRight />
						</StyledLink>
					</MenuRow>
				);
			})}
			<LogoutRow>
				{sessionTimeText && (
					<div>
						<AiFillClockCircle color="#a4a6a8" size={16} />
						{sessionTimeText}
					</div>
				)}
				<button onClick={logout}>
					Log Out <FaArrowAltCircleRight color="#a4a6a8" size={16} />
				</button>
			</LogoutRow>
		</MenuWrapper>
	);
};

export default MenuList;

const MenuWrapper = styled.div<{ $fullScreen: boolean }>`
	position: ${({ $fullScreen }) => ($fullScreen ? 'fixed' : 'absolute')};
	width: ${({ $fullScreen }) => ($fullScreen ? '100%' : '400px')};
	right: 0;
	color: ${({ theme }) => theme.colors.darkGrey};
	background-color: ${({ theme }) => theme.colors.white};
	z-index: 61;
	margin-top: 10px;
	border-bottom-left-radius: 4px;
	border-bottom-right-radius: 4px;
	overflow: hidden;
`;

const MenuRow = styled.div`
	display: flex;
	padding: 16px;
	border-bottom: 1px solid #efefef;
`;

const LogoutRow = styled(MenuRow)`
	justify-content: flex-end;
	align-items: center;
	font-size: 0.8rem;
	cursor: pointer;

	div {
		display: flex;
		align-items: center;
		flex-grow: 1;
		color: #a4a6a8;

		svg {
			margin-right: 5px;
		}
	}

	button {
		display: flex;
		align-items: center;
	}
	svg {
		margin-left: 5px;
	}
`;

const NameWrapper = styled.div`
	padding-left: 12px;

	strong {
		color: ${({ theme }) => theme.colors.darkGrey};
		font-weight: 600;
	}
	i {
		color: ${({ theme }) => theme.colors.lightGrey};
		font-style: normal;
		font-size: 0.8rem;
	}
`;

const UnverifiedLink = styled(Link)`
	display: inline-block;
	padding: 10px 10px;
	color: ${({ theme }) => theme.colors.darkGrey};
	border-radius: 4px;
	border: 1px solid ${({ theme }) => theme.colors.danger};
	font-weight: 600;
`;

const LinkWrapper = styled.div`
	display: flex;
	flex-grow: 1;
	justify-content: flex-end;
`;

const StyledLink = styled.span`
	flex: 1;
	display: flex;
	justify-content: space-between;
	align-items: center;
	cursor: pointer;

	img {
		display: inline-block;
		margin-right: 1rem;
	}

	div {
		display: flex;
		align-items: center;
	}
`;

const Pill = styled.span<{ $color }>`
	display: inline-block;
	padding: 1px 6px 2px;
	color: white;
	background-color: ${({ $color }) => $color};
	border-radius: 10px;
	margin-left: 10px;
	font-size: 0.8rem;
`;
